/**
 * Core types and Zod schemas for the bandit framework.
 *
 * Terminology:
 * - An **experiment** is one adaptive A/B test, identified by the pair
 *   `(courseId, experimentId)`. Experiments never span course runs; rerunning
 *   one next year means a new registry entry under the new courseId.
 * - An **arm** is one variant of the decision under test.
 * - A **cohort** is an independent sub-population with its own posterior over
 *   a (possibly restricted) arm set. Observations never mix across cohorts.
 * - An **event** is the single ground-truth observation a student contributes:
 *   one Firestore doc keyed by their uid, owner-writable only.
 */

import {z} from 'zod';

/** Parameters of a Beta distribution. Both must be strictly positive. */
export interface BetaParams {
  /** Successes pseudo-count (prior + observed successes). */
  alpha: number;
  /** Failures pseudo-count (prior + observed failures). */
  beta: number;
}

/** The cohort every student belongs to unless a call site routes them elsewhere. */
export const DEFAULT_COHORT = 'default';

/**
 * How a student's arm was chosen.
 * - `sampled`: Thompson-sampled from the live posterior. Only these events
 *   ever update the posterior — they are the randomized, causal evidence.
 * - `forced`: deterministically chosen by a component-level policy (e.g. a
 *   profile trait). Logged for observational analysis, never folded into
 *   the posterior.
 */
export const ASSIGNMENT_TYPES = ['sampled', 'forced'] as const;

/** See {@link ASSIGNMENT_TYPES}. */
export type AssignmentType = (typeof ASSIGNMENT_TYPES)[number];

/**
 * Static definition of one experiment. Lives in code (the registry) so both
 * the webapp and the refresh function share a single source of truth for
 * structure. Lifecycle state (active/paused/concluded) lives in Firestore,
 * not here — see {@link banditControlSchema}.
 */
export interface BanditExperimentDefinition {
  /** Course run this experiment belongs to. Part of the experiment's identity. */
  courseId: string;
  /** Short kebab-case ID, unique within the course (e.g. `mathide-layout`). */
  experimentId: string;
  /** One-line human description shown on the admin dashboard. */
  description: string;
  /** Arm names, unique within the experiment. At least two. */
  arms: readonly string[];
  /** Prior Beta parameters applied to every (cohort, arm) posterior. */
  prior: BetaParams;
  /**
   * Per-arm priors that replace `prior` for the arms they name, for an
   * experimenter who believes one arm is better before any data (#5230).
   * Read through `armPrior`. When omitted, every arm uses `prior`.
   */
  armPriors?: Readonly<Record<string, BetaParams>>;
  /**
   * Reward window in hours. A student counts as a success when their solve
   * lands in the half-open interval `(exposedAt, exposedAt + window]`; an
   * exposure with no solve becomes a failure once `exposedAt + window < now`.
   * Exposures still inside the window are pending, not yet observations.
   */
  rewardWindowHours: number;
  /**
   * Declared cohorts. Must include {@link DEFAULT_COHORT}. Call sites may only
   * route students into cohorts declared here — the aggregator enumerates
   * this list when recomputing posteriors, so an undeclared cohort's events
   * would be silently invisible.
   */
  cohorts: readonly string[];
  /**
   * Declared cohorts whose students get an even coin flip over the arms
   * instead of a Thompson sample: a holdout whose plain randomization keeps
   * later comparisons unbiased. Their events are still `sampled`. Omitted:
   * every cohort is Thompson-sampled.
   */
  uniformCohorts?: readonly string[];
  /**
   * The least chance any arm has of being picked in a Thompson-sampled
   * cohort. Arms the posterior would pick less often are raised to it and the
   * others scaled down to match (probability clipping), so a losing arm keeps
   * getting students and every recorded `assignmentProbability` is at least
   * this. At most 1 / the number of arms. Omitted: plain Thompson sampling.
   */
  minArmProbability?: number;
  /**
   * Builds an app-relative URL that renders this experiment's surface with
   * the given arm forced via the `?arm=` override (write-free preview). Used
   * by the admin dashboard to link each arm's UX. When omitted, the dashboard
   * shows no preview links for this experiment.
   */
  previewPath?: (arm: string) => string;
}

/**
 * The single ground-truth observation one student contributes to one
 * experiment. Stored at `bandits/{courseId}/experiments/{expId}/events/{uid}`
 * — the uid-as-doc-ID is what structurally bounds any student's influence to
 * one observation. `TTimestamp` abstracts over the client and admin SDK
 * Firestore Timestamp types.
 */
export interface BanditEvent<TTimestamp> {
  /** Arm the student was assigned. */
  arm: string;
  /** Cohort the student was routed into at assignment time. */
  cohort: string;
  /** Whether the arm was Thompson-sampled or policy-forced. */
  assignmentType: AssignmentType;
  /**
   * The chance the assignment step had of picking this arm (1 for forced),
   * for inverse-propensity weighting in later analyses. Absent on events
   * written before it was recorded.
   */
  assignmentProbability?: number;
  /** Server time when the student first saw the experiment's surface. */
  exposedAt: TTimestamp;
  /** Server time of the student's success, if any. */
  solvedAt?: TTimestamp;
  /**
   * Client-computed "solvedAt fell within the reward window" flag, denormalized
   * because Firestore queries cannot compare two document fields. Written by
   * the student and therefore only as honest as they are — which is the
   * accepted threat model (self-falsification moves exactly one observation).
   */
  solvedInWindow?: boolean;
  /** Event doc format version, for future migrations. */
  schemaVersion: number;
}

/** Current {@link BanditEvent.schemaVersion} written by the client. */
export const BANDIT_EVENT_SCHEMA_VERSION = 1;

/** Zod schema for {@link BetaParams}. */
export const betaParamsSchema = z.object({
  alpha: z.number().positive(),
  beta: z.number().positive(),
});

/**
 * Per-(cohort, arm) observation counts, recomputed from scratch on every
 * refresh. `pending` exposures are inside the reward window and not yet
 * observations; `forced*` counts are observational only.
 */
export const banditArmCountsSchema = z.object({
  successes: z.number().int().nonnegative(),
  failures: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  forcedExposures: z.number().int().nonnegative(),
  forcedSuccesses: z.number().int().nonnegative(),
});

/** See {@link banditArmCountsSchema}. */
export type BanditArmCounts = z.infer<typeof banditArmCountsSchema>;

/**
 * The posterior snapshot doc at `bandits/{courseId}/experiments/{expId}`.
 * Written only by the refresh function; a disposable cache over the events
 * collection. `computedAt`/`refreshStartedAt` are Firestore Timestamps and
 * are validated by the reader, not this schema, because their concrete type
 * differs between the client and admin SDKs.
 */
export const banditSnapshotSchema = z.object({
  /** cohort → arm → posterior Beta parameters. */
  posteriors: z.record(z.string(), z.record(z.string(), betaParamsSchema)),
  /** cohort → arm → raw counts backing the posteriors. */
  counts: z.record(z.string(), z.record(z.string(), banditArmCountsSchema)),
  /**
   * Events the recompute could not attribute to a declared (cohort, arm) —
   * the tamper/bug smoke detector. Should be 0.
   */
  droppedEvents: z.number().int().nonnegative(),
  computedAt: z.unknown(),
  refreshStartedAt: z.unknown().optional(),
});

/** See {@link banditSnapshotSchema}. */
export type BanditSnapshot = z.infer<typeof banditSnapshotSchema>;

/** Lifecycle states of an experiment. */
export const BANDIT_STATUSES = ['active', 'paused', 'concluded'] as const;

/** See {@link BANDIT_STATUSES}. */
export type BanditStatus = (typeof BANDIT_STATUSES)[number];

/**
 * The admin-written control doc at
 * `bandits/{courseId}/experiments/{expId}/control/doc`.
 * - `active`: new students are Thompson-sampled.
 * - `paused`: new students get the experiment's first arm; nothing is recorded.
 * - `concluded`: new students get `winner`; nothing is recorded; refreshes
 *   are refused and the snapshot freezes as the permanent record.
 * A missing control doc means `active`.
 */
export const banditControlSchema = z.object({
  status: z.enum(BANDIT_STATUSES),
  /** Winning arm, required once `status` is `concluded`. */
  winner: z.string().optional(),
  /** Uid of the admin who last changed the status. */
  decidedBy: z.string().optional(),
  decidedAt: z.unknown().optional(),
});

/** See {@link banditControlSchema}. */
export type BanditControl = z.infer<typeof banditControlSchema>;
